import { toast } from 'react-toastify';
import { shipments } from '../api/shipments';
import {
  shipmentTrackingReports,
  type BulkDownloadSummary,
  type ReviewableDocument,
  type ShipmentTrackingReport,
} from '../api/shipment-tracking-reports';

/**
 * Downloading a parcel's paperwork from the office side.
 *
 * One place for what used to be four copies of the same label-download handler,
 * plus the packing slip, which used to open a page of its own with a print
 * button and now comes straight down as a PDF. The API records each download
 * against the parcel - that record is what lets a before-pack acknowledgement
 * be filed - and returns the files ready to save.
 */

export type DocumentChoice = 'LABEL' | 'PACKING_SLIP' | 'BROCHURE' | 'BOTH' | 'ALL';

/**
 * The download menu. 'BOTH' (label + packing slip as two files) is not offered
 * here any more; the before-pack review modal still has its own button for it.
 */
export const DOCUMENT_CHOICES: { id: DocumentChoice; label: string; hint: string }[] = [
  { id: 'LABEL', label: 'Label', hint: 'The carrier label as stored - PDF, PNG or ZPL' },
  { id: 'PACKING_SLIP', label: 'Packing slip (PDF)', hint: 'Saved straight away, no preview page' },
  { id: 'BROCHURE', label: 'Brochure', hint: "The order's category brochure, or the default one, from Company Settings" },
  {
    id: 'ALL',
    label: 'Label + brochure + packing slip',
    hint: 'One PDF: the label, then the packing slip, then the company brochure',
  },
];

const documentsFor = (choice: DocumentChoice): ReviewableDocument[] =>
  choice === 'BROCHURE'
    ? []
    : choice === 'BOTH' || choice === 'ALL'
      ? ['LABEL', 'PACKING_SLIP']
      : [choice];

/**
 * Whether label / packing slip downloads and prints ask the question below,
 * from Company Settings → AutoBatch → "Ask before downloading paperwork". On
 * by default. Off, the download goes straight ahead and the shipment is marked
 * batched, so AutoBatch skips it (its paperwork is already out).
 * Cached like the company time zone, so it applies from first paint.
 */
const DOWNLOAD_PROMPT_KEY = 'autoBatchDownloadPrompt';
const readDownloadPrompt = (value: unknown): boolean => value !== false && value !== 'false';

let showDownloadPrompt = (() => {
  try {
    return readDownloadPrompt(localStorage.getItem(DOWNLOAD_PROMPT_KEY));
  } catch {
    return true;
  }
})();

/** Call whenever the company profile loads or its AutoBatch settings are saved. */
export function setDownloadPrompt(value: unknown) {
  showDownloadPrompt = readDownloadPrompt(value);
  try {
    localStorage.setItem(DOWNLOAD_PROMPT_KEY, String(showDownloadPrompt));
  } catch {
    /* private mode - the in-memory value still applies */
  }
}

/**
 * OK marks the shipment batched, so the nightly AutoBatch does not print its
 * paperwork a second time; Cancel downloads without marking it. With the
 * question switched off in Company Settings, every download is marked, as OK.
 */
const askToSkipAutoBatch = async (shipmentId: string | string[], question: string) => {
  if (showDownloadPrompt && !window.confirm(question)) return;
  try {
    // An order-wide download spans every shipment it includes.
    await Promise.all((Array.isArray(shipmentId) ? shipmentId : [shipmentId]).map((sid) => shipments.markBatched(sid)));
  } catch (err) {
    // The paperwork is still wanted; the batch flag is not worth losing it over.
    console.error('Could not mark the shipment as batched', err);
    toast.warn('The shipment could not be marked as batched. AutoBatch may still pick it up.');
  }
};

/** Hand a base64 file to the browser as a download. */
export const saveBase64File = (base64: string, contentType: string, filename: string) => {
  const link = document.createElement('a');
  link.href = `data:${contentType};base64,${base64}`;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * Open the browser's print dialog for a PDF, without saving it.
 *
 * Loaded into a hidden iframe from a blob URL; the frame is removed a minute
 * later rather than on afterprint, which some browsers fire before the
 * dialog has finished spooling.
 */
export const printBase64Pdf = (base64: string) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  printPdfBlob(new Blob([bytes], { type: 'application/pdf' }));
};

/** The same, for a PDF already in hand as a Blob (e.g. an API download). */
export const printPdfBlob = (pdf: Blob) => {
  const url = URL.createObjectURL(pdf.type === 'application/pdf' ? pdf : new Blob([pdf], { type: 'application/pdf' }));
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  frame.src = url;
  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } catch {
      // A browser that will not script its PDF viewer: open it in a tab instead.
      window.open(url, '_blank');
    }
  };
  document.body.appendChild(frame);
  setTimeout(() => {
    frame.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
};

/**
 * Print the files a download returned: PDFs go to the print dialog, anything
 * else (a ZPL/EPL label a browser cannot print) is saved as before, with a note.
 */
const printOrSave = (files: { base64: string; contentType: string; filename: string }[]) => {
  for (const file of files) {
    if (file.contentType === 'application/pdf') {
      printBase64Pdf(file.base64);
    } else {
      saveBase64File(file.base64, file.contentType, file.filename);
      toast.info(`${file.filename} can't be printed from the browser, so it was downloaded for your label printer.`);
    }
  }
};

/** The order the paperwork belongs to, so the AutoBatch question can name it. */
export interface PaperworkOrder {
  orderNumber?: string | null;
  customerName?: string | null;
}

/**
 * "order #112-… (Herbert Meadows)", or "" when neither is known. Names the
 * order and customer rather than a carrier or shipment id: that is what the
 * person at the desk recognises.
 */
const describeOrder = (order?: PaperworkOrder | null) => {
  const number = String(order?.orderNumber || '').replace(/^#/, '').trim();
  const customer = String(order?.customerName || '').trim();
  if (!number && !customer) return '';
  if (!number) return ` for ${customer}`;
  return ` for order #${number}${customer ? ` (${customer})` : ''}`;
};

/** The AutoBatch question, worded for a print or a download. */
const autoBatchQuestion = (print: boolean, what: string, order: PaperworkOrder | null | undefined, parcels: string) =>
  `${print ? 'Printing' : 'Downloading'} the ${what}${describeOrder(order)} - ${parcels}.\n\n` +
  `Are you shipping directly? OK marks the shipment as batched so AutoBatch skips it; ` +
  `Cancel ${print ? 'prints' : 'downloads'} without marking it.`;

export interface DownloadTrackingDocumentsOptions {
  shipmentId: string;
  trackingNumber: string;
  carrier?: string | null;
  choice: DocumentChoice;
  /**
   * Skip the "ship directly, skip AutoBatch?" question. The order page asks it
   * because a label downloaded there is usually going straight on a box; a
   * reviewer fetching the paperwork to check it is not shipping anything.
   */
  askAutoBatch?: boolean;
  /**
   * Send the PDF to the print dialog instead of saving it. The API logs it as
   * a print (source: this page) rather than a download. It does not use up the
   * facility's single label print - that counter is the facility's alone.
   */
  print?: boolean;
  /** Named in the AutoBatch question. */
  order?: PaperworkOrder | null;
}

/**
 * Download the chosen paperwork for one parcel, telling the user about anything
 * that could not be produced. Resolves to the refreshed fulfilment report, or
 * null when the download did not happen (cancelled, or nothing came back).
 */
export const downloadTrackingDocuments = async ({
  shipmentId,
  trackingNumber,
  choice,
  askAutoBatch = true,
  print = false,
  order,
}: DownloadTrackingDocumentsOptions): Promise<ShipmentTrackingReport | null> => {
  // A brochure on its own ships nothing, so there is no AutoBatch question.
  if (askAutoBatch && choice !== 'BROCHURE') {
    const what =
      choice === 'LABEL'
        ? 'label'
        : choice === 'PACKING_SLIP'
          ? 'packing slip'
          : choice === 'ALL'
            ? 'label, brochure and packing slip'
            : 'label and packing slip';
    await askToSkipAutoBatch(shipmentId, autoBatchQuestion(print, what, order, `parcel ${trackingNumber}`));
  }

  let result;
  try {
    // "Both" stays two files: the office wants the label and the slip as they
    // are. "All" is the one joined PDF - label, packing slip, brochure - for
    // printing everything that goes with the box in one go.
    const all = choice === 'ALL';
    result = await shipmentTrackingReports.downloadDocuments(
      trackingNumber,
      documentsFor(choice),
      all,
      all || choice === 'BROCHURE',
      print ? 'PRINT' : undefined,
    );
  } catch (err) {
    // The API client has already toasted the server's reason.
    console.error('Failed to download tracking documents', err);
    return null;
  }

  if (print) printOrSave(result.files);
  else for (const file of result.files) saveBase64File(file.base64, file.contentType, file.filename);
  const reasons = Array.from(new Set(result.unavailable.map((u) => u.reason)));
  if (result.files.length === 0) {
    // Notes too: "no brochure is uploaded" arrives as a note, not a reason.
    toast.error([...reasons, ...result.notes].join('\n') || 'Nothing could be downloaded for this parcel.');
    return null;
  }
  reasons.forEach((reason) => toast.warn(reason));
  result.notes.forEach((note) => toast.info(note));
  return result.report;
};

export interface DownloadShipmentDocumentsOptions {
  /** One shipment, or several for an order-wide download (all marked batched). */
  shipmentId: string | string[];
  /** The shipment's parcels still to ship (not voided), in package order. */
  trackingNumbers: string[];
  carrier?: string | null;
  /**
   * One document per download - labels and slips go to different printers - or
   * 'ALL': every label and slip, parcel by parcel, then the brochure once.
   */
  document: ReviewableDocument | 'ALL';
  /** Print instead of saving; see DownloadTrackingDocumentsOptions.print. */
  print?: boolean;
  /** What was picked, for the order history. Defaults to SHIPMENT. */
  scope?: 'ORDER' | 'SHIPMENT';
  /** Named in the AutoBatch question. */
  order?: PaperworkOrder | null;
  /**
   * Several orders in one PDF (the Orders page's selected rows): each order's
   * tracking numbers, so each gets its own brochure. trackingNumbers must list
   * them in the same order.
   */
  groups?: string[][];
  /**
   * false: no Final Checksheet at the end of the PDF - one order printed or
   * saved from its own row menu, where a tally of a single order is pointless.
   */
  checksheet?: boolean;
}

/**
 * Every label, or every packing slip, of one shipment as a single PDF - the label
 * and packing-slip buttons on a shipment of the order page - or with 'ALL',
 * everything in one: label 1, slip 1, label 2, slip 2, ..., then the brochure
 * once. One AutoBatch question for the shipment, not one per parcel. Resolves false when nothing was downloaded.
 */
export const downloadShipmentDocuments = async ({
  shipmentId,
  trackingNumbers,
  document,
  print = false,
  scope = 'SHIPMENT',
  order,
  groups,
  checksheet,
}: DownloadShipmentDocumentsOptions): Promise<boolean> => {
  if (trackingNumbers.length === 0) return false;
  const what =
    document === 'LABEL'
      ? 'labels'
      : document === 'PACKING_SLIP'
        ? 'packing slips'
        : 'labels, packing slips and brochure';
  const shipmentCount = Array.isArray(shipmentId) ? shipmentId.length : 1;
  const orderCount = groups?.length ?? 1;
  const parcels =
    `${trackingNumbers.length} parcel${trackingNumbers.length === 1 ? '' : 's'}` +
    (orderCount > 1
      ? ` across ${orderCount} orders`
      : shipmentCount > 1
        ? ` in ${shipmentCount} shipments`
        : '');
  await askToSkipAutoBatch(shipmentId, autoBatchQuestion(print, what, order, parcels));

  let result;
  try {
    result = await shipmentTrackingReports.downloadDocumentsBulk(
      trackingNumbers,
      document === 'ALL' ? ['LABEL', 'PACKING_SLIP'] : [document],
      document === 'ALL',
      { intent: print ? 'PRINT' : undefined, scope },
      groups,
      { checksheet },
    );
  } catch (err) {
    // The API client has already toasted the server's reason.
    console.error('Failed to download the shipment documents', err);
    return false;
  }

  if (print) printOrSave(result.files);
  else for (const file of result.files) saveBase64File(file.base64, file.contentType, file.filename);
  // Problems stay on screen until closed: a short print run is easy to miss if
  // the only sign of it fades while the printer is still going.
  const sticky = { autoClose: false as const };
  const reasons = Array.from(new Set(result.unavailable.map((u) => u.reason)));
  if (result.files.length === 0) {
    toast.error(
      reasons.join('\n') || `No ${what} could be downloaded for ${orderCount > 1 ? 'these orders' : 'this shipment'}.`,
      sticky,
    );
    return false;
  }
  result.summary
    .filter((s) => s.missing.length > 0)
    .forEach((s) => toast.warn(shortRunMessage(s, s.document === 'LABEL' ? 'labels' : 'packing slips'), sticky));
  reasons.forEach((reason) => toast.warn(reason, sticky));
  result.notes.forEach((note) => toast.info(note));
  return true;
};

/** Tracking numbers listed in the short-run message before "+N more". */
const MISSING_LISTED = 10;

/** "9 of 10 labels are in this PDF. Not in it: 1Z…" */
const shortRunMessage = (summary: BulkDownloadSummary, what: string) => {
  const listed = summary.missing.slice(0, MISSING_LISTED).join(', ');
  const more =
    summary.missing.length > MISSING_LISTED ? ` +${summary.missing.length - MISSING_LISTED} more` : '';
  const head =
    summary.included === 0
      ? `None of the ${summary.requested} ${what} could go into a PDF.`
      : `${summary.included} of ${summary.requested} ${what} are in this PDF.`;
  return `${head} Not in it: ${listed}${more}`;
};
